"use client";
import { useContext } from "react";

import { usePathname } from "next/navigation";

import { HEADER_TYPES, SUBPUSH_PAGES, TT_PAGES } from "@/constants/";
import CartContext from "@/context/CartContext";
import UserContext from "@/context/UserContext";
import { useRewards } from "@/hooks/useRewards";
import { FactoryProps } from "@/types/common";
import { getLayoutDataBySlug } from "@/utils/getLayoutDataBySlug";
import { getPromoByName } from "@/utils/getPromoData";
import { currentTheme } from "@/utils/theming";

import Header from "./Header";
import HeaderReallySimple from "./HeaderReallySimple";
import HeaderSecondary from "./HeaderSecondary";
import HeaderSimple from "./HeaderSimple";

export default function HeaderFactory({ cmsData }: FactoryProps) {
  const pathname = usePathname();
  const { siteTheme } = useContext(CartContext);
  const isCartOrCheckoutPage =
    pathname === "/checkout" ||
    pathname === "/checkout/" ||
    pathname === "/checkout/cart" ||
    pathname === "/checkout/cart/";
  /*
   * Checkout/cart's header theme comes from the cart's `site` field
   * (set via CartContext by CheckoutGuest/CheckoutRegistered from GraphQL,
   * or CartContent from the REST cart payload), not the URL — these pages
   * are static routes regardless of cart contents.
   */
  const theme = isCartOrCheckoutPage
    ? siteTheme === "men"
      ? "men"
      : "master"
    : currentTheme(pathname);
  const { allContentfulLayoutData, allContentfulHeaderRibbon } = cmsData;
  const layoutDataBySlug = getLayoutDataBySlug(pathname, allContentfulLayoutData);
  const ribbonData = allContentfulHeaderRibbon.find(
    (node) => node?.site === (theme === "men" ? "men" : "women")
  );
  const promoCf = getPromoByName("blackfriday2026-1");
  const isHeaderReallySimple = layoutDataBySlug?.header_type === HEADER_TYPES.REALLYSIMPLE;
  const isHeaderSimple = layoutDataBySlug?.header_type === HEADER_TYPES.SIMPLE;
  const isHeaderSecondary = layoutDataBySlug?.header_type === HEADER_TYPES.SECONDARY;
  const isRibbonSub = SUBPUSH_PAGES.includes(pathname);
  const hideRibbonPaths = ["/hair/brand/lp/core/hair-growth-product-comparison/"];
  const initialWithRibbon = hideRibbonPaths.includes(pathname)
    ? false
    : !(layoutDataBySlug?.has_ribbon === false);
  const isRibbonReallySimple = TT_PAGES.includes(pathname);
  const { nextReward } = useRewards(useContext(UserContext));

  let initialPromoVariant = isRibbonSub ? "subpush" : "";
  if (nextReward?.segment_name) {
    initialPromoVariant = nextReward.segment_name;
  }

  // Pages with custom headers rendered within their own component tree.
  if (
    theme === "app" ||
    pathname.includes("/order-confirmation") ||
    /\/hair\/(brand|men|women)\/quiz\b/.test(pathname)
  ) {
    return null;
  }

  if (isHeaderReallySimple) {
    return <HeaderReallySimple withRibbon={isRibbonReallySimple} theme={theme} />;
  }

  if (isHeaderSimple) {
    return <HeaderSimple pathname={pathname} theme={theme} />;
  }

  if (isHeaderSecondary) {
    return (
      <HeaderSecondary pathname={pathname} theme={theme} layoutDataBySlug={layoutDataBySlug} />
    );
  }

  return (
    <Header
      pathname={pathname}
      theme={theme}
      contentfulHeaderRibbon={ribbonData}
      initialWithRibbon={initialWithRibbon}
      initialPromoVariant={initialPromoVariant}
      promoCf={promoCf}
    />
  );
}
